import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation } from '@tanstack/react-query';
import { JobsService } from 'app/API';
import { getClaimJobError } from 'app/helpers/getClaimJobError';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';

type TOptions = {
  workspaceId: string;
  onSuccess: () => void | Promise<void>;
};

export const useJobStatusActions = ({ workspaceId, onSuccess }: TOptions) => {
  const { call } = useAPI();
  const { openModal } = useModalContext();

  const claimMutation = useMutation({
    mutationFn: (jobId: string) => call(() => JobsService.claimJob({ wsid: workspaceId, jobId })),
    onSuccess: (response) => {
      const failure = getClaimJobError(response);
      if (failure) {
        openModal(ModalNamesEnum.GenericErrorModal, failure);
        return;
      }
      return onSuccess();
    },
    onError: (error) => {
      const failure = getClaimJobError(error);
      if (failure) {
        openModal(ModalNamesEnum.GenericErrorModal, failure);
        return;
      }
      notifyApiCallError(error);
    },
  });

  const markAsCompleteMutation = useMutation({
    mutationFn: (jobId: string) => call(() => JobsService.markJobAsComplete({ wsid: workspaceId, jobId })),
    onSuccess: () => onSuccess(),
    onError: notifyApiCallError,
  });

  const markAsEditedMutation = useMutation({
    mutationFn: (jobId: string) => call(() => JobsService.markJobAsReadyForProofreading({ wsid: workspaceId, jobId })),
    onSuccess: () => onSuccess(),
    onError: notifyApiCallError,
  });

  const markAsProofreadMutation = useMutation({
    mutationFn: (jobId: string) => call(() => JobsService.markJobAsReadyForFinal({ wsid: workspaceId, jobId })),
    onSuccess: () => onSuccess(),
    onError: notifyApiCallError,
  });

  return {
    claim: claimMutation.mutate,
    markAsComplete: markAsCompleteMutation.mutate,
    markAsEdited: markAsEditedMutation.mutate,
    markAsProofread: markAsProofreadMutation.mutate,
    isPending:
      claimMutation.isPending ||
      markAsCompleteMutation.isPending ||
      markAsEditedMutation.isPending ||
      markAsProofreadMutation.isPending,
  };
};
